import { datadogRum } from '@datadog/browser-rum';

import { toast } from '@askable/ui/core/sonner';
import { getSocketCloseCode } from 'network/socketClose';
import i18n from 'src/i18n';

import type { GraphQLError } from 'graphql';

type GraphqlName = string;

export type KNOWN_ERROR_TYPES = 'no_connected_team' | 'user_not_on_team';

export const KNOWN_ERROR_TYPES_ARRAY: ReadonlyArray<KNOWN_ERROR_TYPES> = [
  'no_connected_team',
  'user_not_on_team',
] as const;

const criticalErrors: GraphqlName[] = ['viewer', 'maybeCreateKindeOrganization'];

const OFFLINE_TOAST_ID = 'network-offline';

const handleNetworkError = (networkError: Error) => {
  // A socket close is the subscription client's to retry, report and tell the user about.
  if (getSocketCloseCode(networkError) !== undefined) return;

  // fetch rejects with a TypeError ("Failed to fetch", "Load failed") when the request never completes.
  const offline = !navigator.onLine || networkError instanceof TypeError;
  datadogRum.addError(networkError, { source: 'graphql-network', kind: offline ? 'offline' : 'other' });

  const hasMessage =
    networkError.message && networkError.message !== 'Network request failed' && networkError.message !== 'undefined';
  if (!offline && hasMessage) {
    toast.error(networkError.message);
    return;
  }
  // Every query on screen fails at once, so they share one toast rather than stacking.
  toast.error(i18n.t('global.offline'), { id: OFFLINE_TOAST_ID });
};

export const graphqlErrorHandler = ({
  graphqlErrors,
  networkError,
}: {
  graphqlErrors?: ReadonlyArray<GraphQLError>;
  networkError?: Error;
}) => {
  if (networkError) {
    handleNetworkError(networkError);
    return;
  }

  for (const error of graphqlErrors ?? []) {
    const isCriticalError = error.path?.some(path => criticalErrors.includes(path as GraphqlName));
    const isViewerQuery = error.path?.some(path => path === 'viewer');

    if (
      ((error.extensions?.code === 401 && isViewerQuery) ||
        error?.message === 'invalid token' ||
        error?.message === 'jwt expired') &&
      ['/whoops', '/logout'].every(v => window.location.pathname !== v)
    ) {
      // If error is 401 on Viewer query, redirect user to logout page
      window.location.replace('/logout');
      return;
    }

    // If error is critical,
    // Make sure this runs before the rest so it will inform info to user, and the rest might redirect user.
    // toast('Something went wrong. Please contact to support team.');
    if (isCriticalError && ['/whoops', '/logout'].every(v => window.location.pathname !== v)) {
      // If error is critical, redirect user to whoops page
      // error.extension should be object in type, but it can be undefined
      window.location.replace(`/whoops?error=${error.extensions?.type}`);
    }
    break;
  }
};
